import React from 'react';
import { CustomerOption, NewCloseData, TrialBalancePeriodRecord } from './types';
import { formatNumber } from './utils';
import { CloseContextSummary } from './CloseContextSummary';

interface Step6FinalStatusProps {
  loading: boolean;
  showTrialBalance: boolean;
  trialBalanceData: TrialBalancePeriodRecord[];
  customers: CustomerOption[];
  newCloseData: NewCloseData;
  onBack?: () => void;
  onSendToTax?: () => void;
  onFinish?: () => void;
}

const Step6FinalStatus: React.FC<Step6FinalStatusProps> = ({
  loading,
  showTrialBalance,
  trialBalanceData,
  customers,
  newCloseData,
  onBack,
  onSendToTax,
  onFinish,
}) => {
  return (
    <>
      {/* Header with buttons */}
      <div className="flex items-start justify-end mb-4">
        <div className="flex gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Буцах
            </button>
          )}
          {onSendToTax && (
            <button
              onClick={onSendToTax}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              disabled={loading}
            >
              Татварлуу илгээх
            </button>
          )}
          {onFinish && (
            <button
              onClick={onFinish}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              Дуусгах
            </button>
          )}
        </div>
      </div>

      <CloseContextSummary customers={customers} newCloseData={newCloseData} />

      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Сүүлийн байдал - Trial Balance</h3>
        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
            <p className="text-blue-800 font-medium">Trial Balance мэдээлэл уншиж байна...</p>
          </div>
        )}
      </div>

      {/* TrialBalancePeriod Detailed Table */}
      {showTrialBalance && trialBalanceData.length > 0 ? (
            <div className="overflow-x-auto mb-6">
              <h4 className="text-md font-semibold text-gray-700 mb-3">Trial Balance Period (Дэлгэрэнгүй)</h4>
              <table className="min-w-full divide-y divide-gray-200 text-sm border border-gray-300">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Дансны код</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Дансны нэр</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300">Ангилал</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Дебет C1</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Кредит C1</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Дебет</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Кредит</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Дебет C2</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300">Кредит C2</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trialBalanceData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-900 border border-gray-300">{row.AccountCode}</td>
                      <td className="px-3 py-2 text-gray-900 border border-gray-300">{row.AccountName}</td>
                      <td className="px-3 py-2 text-gray-600 border border-gray-300">{row.AccountCategoryName}</td>
                      <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.DebitAmountC1)}</td>
                      <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CreditAmountC1)}</td>
                      <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.DebitAmount)}</td>
                      <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CreditAmount)}</td>
                      <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.DebitAmountC2)}</td>
                      <td className="px-3 py-2 text-right text-gray-900 border border-gray-300">{formatNumber(row.CreditAmountC2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 sticky bottom-0">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 font-semibold text-gray-900 border border-gray-300">Нийт:</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                      {formatNumber(trialBalanceData.reduce((sum, row) => sum + (row.DebitAmountC1 || 0), 0))}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                      {formatNumber(trialBalanceData.reduce((sum, row) => sum + (row.CreditAmountC1 || 0), 0))}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                      {formatNumber(trialBalanceData.reduce((sum, row) => sum + (row.DebitAmount || 0), 0))}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                      {formatNumber(trialBalanceData.reduce((sum, row) => sum + (row.CreditAmount || 0), 0))}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                      {formatNumber(trialBalanceData.reduce((sum, row) => sum + (row.DebitAmountC2 || 0), 0))}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-300">
                      {formatNumber(trialBalanceData.reduce((sum, row) => sum + (row.CreditAmountC2 || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : !loading && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-6">
              <p className="text-gray-600 text-center">Trial Balance Period мэдээлэл олдсонгүй</p>
            </div>
          )}
    </>
  );
};

export default Step6FinalStatus;
